
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { CandidateFormData } from "@/utils/candidate";
import { CVUploadZone } from "./CVUploadZone";
import { useTranslation } from "react-i18next";
import { Linkedin } from "lucide-react";

interface CandidateFormFieldsProps {
	formData: CandidateFormData;
	onChange: (field: keyof CandidateFormData, value: string) => void;
	onFileChange: (file: File | null) => void;
	disabled?: boolean;
	onFetchLinkedIn?: () => void;
}

export const CandidateFormFields = ({ 
	formData, 
	onChange, 
	onFileChange,
	disabled = false,
	onFetchLinkedIn
}: CandidateFormFieldsProps) => {
	const { t } = useTranslation();
	
	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		onChange(e.target.name as keyof CandidateFormData, e.target.value);
	};
	
	return (
		<div className="space-y-4">
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				<div className="space-y-2">
					<Label htmlFor="firstName">{t("candidates.newCandidateModal.firstNameLabel")}</Label>
					<Input
						id="firstName"
						name="firstName"
						value={formData.firstName}
						onChange={handleInputChange}
						placeholder={t("candidates.newCandidateModal.firstNamePlaceholder")}
						disabled={disabled}
					/>
				</div>
				<div className="space-y-2">
					<Label htmlFor="lastName">{t("candidates.newCandidateModal.lastNameLabel")}</Label>
					<Input
						id="lastName"
						name="lastName"
						value={formData.lastName}
						onChange={handleInputChange}
						placeholder={t("candidates.newCandidateModal.lastNamePlaceholder")}
						disabled={disabled}
					/>
				</div>
			</div>
			
			<div className="space-y-2">
				<Label htmlFor="email">{t("candidates.newCandidateModal.emailLabel")}</Label>
				<Input
					type="email"
					id="email"
					name="email"
					value={formData.email}
					onChange={handleInputChange}
					placeholder={t("candidates.newCandidateModal.emailPlaceholder")}
					disabled={disabled}
				/>
			</div>
			
			<div className="space-y-2">
				<Label htmlFor="phone">{t("candidates.newCandidateModal.phonePlaceholder")}</Label>
				<Input
					type="tel"
					id="phone"
					name="phone"
					value={formData.phone}
					onChange={handleInputChange}
					placeholder={t("candidates.newCandidateModal.phonePlaceholder")}
					disabled={disabled}
				/>
			</div>
			
			<div className="space-y-2">
				<Label htmlFor="role">{t("candidates.newCandidateModal.functionLabel")}</Label>
				<Input
					id="role"
					name="role"
					value={formData.role}
					onChange={handleInputChange}
					placeholder={t("candidates.newCandidateModal.functionPlaceholder")}
					disabled={disabled}
				/>
			</div>
			
			<div className="space-y-2">
				<Label htmlFor="skills">{t("candidates.newCandidateModal.skillsLabel")}</Label>
				<Input
					id="skills"
					name="skills"
					value={formData.skills}
					onChange={handleInputChange}
					placeholder={t("candidates.newCandidateModal.skillsPlaceholder")}
					disabled={disabled}
				/>
				<p className="text-xs text-gray-500">{t("candidates.newCandidateModal.skillsSeparator")}</p>
			</div>
			
			<div className="space-y-2">
				<Label htmlFor="linkedin">{t("candidates.newCandidateModal.linkedinLabel")}</Label>
				<div className="flex gap-2">
					<div className="relative flex-grow">
						<div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
							<Linkedin className="h-4 w-4 text-gray-500" />
						</div>
						<Input
							id="linkedin"
							name="linkedin"
							value={formData.linkedin}
							onChange={handleInputChange}
							placeholder={t("candidates.newCandidateModal.linkedinPlaceholder")}
							className="pl-10"
							disabled={disabled}
						/>
					</div>
					{onFetchLinkedIn && (
						<Button 
							type="button" 
							variant="outline" 
							onClick={onFetchLinkedIn}
							disabled={disabled || !formData.linkedin}
						>
							Fetch
						</Button>
					)}
				</div>
			</div>
			
			<div className="space-y-2">
				<Label htmlFor="summary">{t("candidates.newCandidateModal.summaryLabel")}</Label>
				<Textarea
					id="summary"
					name="summary"
					value={formData.summary}
					onChange={handleInputChange}
					placeholder={t("candidates.newCandidateModal.summaryPlaceholder")}
					className="min-h-[100px]"
					disabled={disabled}
				/>
			</div>
			
			<div className="space-y-2">
				<Label>{t("candidates.newCandidateModal.uploadLabel")}</Label>
				<CVUploadZone
					onFileChange={onFileChange}
					selectedFile={formData.cvFile}
					disabled={disabled}
				/>
			</div>
		</div>
	);
};
